import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CreditCard, Loader2, AlertCircle, Clock, ShieldCheck, Zap, User, Building2, Lock, Mail, ArrowLeft, UserCircle, Banknote, CheckCircle } from 'lucide-react';
import CompleteAuthModal from './CompleteAuthModal';
import { useAuth } from '../context/CompleteAuthContext';
import { computePayerFee, METHOD_LABEL } from '../lib/payerFee';
import PayoLogo from './PayoLogo';
import { toast } from 'sonner';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
import { API_URL as BACKEND_URL } from '../config/api';

const RESUME_KEY = 'qs_pay_resume';
function saveResume(sessionId, payerName, path) {
  // path: 'payo' (generic, came from welcome) | 'ach' (came from bank gate, force ACH after auth)
  sessionStorage.setItem(RESUME_KEY, JSON.stringify({ sessionId, payerName, path, ts: Date.now() }));
}
function loadResume() {
  try {
    const raw = sessionStorage.getItem(RESUME_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (Date.now() - d.ts > 30 * 60 * 1000) { sessionStorage.removeItem(RESUME_KEY); return null; }
    return d;
  } catch { return null; }
}
function clearResume() { sessionStorage.removeItem(RESUME_KEY); }

// ── Returning-payer recovery ─────────────────────────────────────────────
// We persist the {name, email} a payer entered at Step 2 in localStorage so
// that closing the tab and coming back via the original link (or a reminder
// email) drops them straight into Step 3 with their identity prefilled. The
// server is the source of truth for "have they actually paid?" — we always
// call /payer-status on resume before deciding what UI to show.
const ENROLL_LOCAL_PREFIX = 'qs_enroll:';
const ENROLL_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
function saveLocalEnrollment(sessionId, name, email) {
  try {
    localStorage.setItem(
      `${ENROLL_LOCAL_PREFIX}${sessionId}`,
      JSON.stringify({ name, email, ts: Date.now() }),
    );
  } catch { /* localStorage unavailable (private mode) — best-effort */ }
}
function loadLocalEnrollment(sessionId) {
  try {
    const raw = localStorage.getItem(`${ENROLL_LOCAL_PREFIX}${sessionId}`);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (!d?.email || !d?.name || Date.now() - (d.ts || 0) > ENROLL_TTL_MS) {
      localStorage.removeItem(`${ENROLL_LOCAL_PREFIX}${sessionId}`);
      return null;
    }
    return d;
  } catch { return null; }
}

// Basic email format check used at the boundary. Backend re-validates.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (s) => !!s && EMAIL_RE.test(s.trim()) && s.trim().length <= 200;

/* ─── Checkout form (unchanged) ─── */
const CheckoutForm = ({ sessionId, amountCents, displayTotal, paymentIntentId, internalPaymentId, payerName, onSuccess, onPending, onMethodChange, isGuest }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleElementChange = (event) => {
    if (event.value?.type && onMethodChange) onMethodChange(event.value.type === 'us_bank_account' ? 'ach' : 'card');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setIsProcessing(true);
    setErrorMessage(null);
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({ elements, redirect: 'if_required' });
      if (error) { setErrorMessage(error.message); setIsProcessing(false); return; }
      if (!paymentIntent) { setErrorMessage('Payment could not be completed.'); setIsProcessing(false); return; }
      const res = await fetch(`${BACKEND_URL}/api/quick-split/public/${sessionId}/confirm`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_intent_id: paymentIntentId, internal_payment_id: internalPaymentId }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'pending_ach') onPending(data.internal_payment_id || internalPaymentId, payerName, amountCents);
        else onSuccess(data.internal_payment_id || internalPaymentId, payerName, data.host_name || '', amountCents);
      } else if (paymentIntent.status === 'succeeded') onSuccess(internalPaymentId, payerName, '', amountCents);
      else if (paymentIntent.status === 'processing') onPending(internalPaymentId, payerName, amountCents);
      else setErrorMessage('Payment could not be completed.');
    } catch { setErrorMessage('An unexpected error occurred.'); }
    finally { setIsProcessing(false); }
  };

  const pmOrder = isGuest ? ['card'] : ['card', 'us_bank_account'];

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="min-h-[200px]">
        <PaymentElement onChange={!isGuest ? handleElementChange : undefined} options={{
          wallets: { applePay: 'auto', googlePay: 'never' },
          layout: { type: 'tabs' },
          paymentMethodOrder: pmOrder,
        }} />
      </div>
      {errorMessage && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="qs-pay-error"><p className="text-sm text-red-700">{errorMessage}</p></div>}
      <Button type="submit" disabled={isProcessing || !stripe || !elements} className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 text-base font-semibold" data-testid="qs-pay-submit-btn">
        {isProcessing ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</> : <><CreditCard className="w-5 h-5 mr-2" /> Pay ${((displayTotal || amountCents) / 100).toFixed(2)}</>}
      </Button>
      <div className="flex items-center justify-center space-x-1.5 text-xs text-gray-400" data-testid="qs-pay-stripe-trust"><ShieldCheck className="w-3 h-3" /><span>Secure payments powered by Stripe</span></div>
    </form>
  );
};

/* ─── Main page ─── */
const QuickSplitPayPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user: authUser } = useAuth();

  const [session, setSession] = useState(null);
  const [step, setStep] = useState('loading');
  const stepRef = useRef('loading');

  // Push browser history entries for each step so native back button works within the flow
  const goToStep = (newStep) => {
    const navigableSteps = ['welcome', 'identity', 'guest_choose_method', 'guest_external_confirm', 'guest_bank_gate', 'payo_choose_method', 'payo_saved_bank', 'payo_new_bank', 'paying', 'venmo_confirm'];
    if (navigableSteps.includes(newStep) && stepRef.current !== newStep) {
      window.history.pushState({ step: newStep, sessionId }, '', window.location.href);
    }
    stepRef.current = newStep;
    setStep(newStep);
  };

  // Listen for browser back button (popstate)
  useEffect(() => {
    const handlePopState = (e) => {
      if (e.state?.step) {
        stepRef.current = e.state.step;
        setStep(e.state.step);
      } else {
        // No step in history — user is leaving the flow. Go to welcome if session loaded.
        if (session) {
          stepRef.current = 'welcome';
          setStep('welcome');
          window.history.pushState({ step: 'welcome', sessionId }, '', window.location.href);
        }
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [session, sessionId]); // loading | welcome | guest_name | auth_name | creating_intent | paying | full | expired | link_expired | error
  const [errorMsg, setErrorMsg] = useState('');
  const [payerName, setPayerName] = useState('');
  const [payerEmail, setPayerEmail] = useState('');
  const [isGuest, setIsGuest] = useState(true);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [internalPaymentId, setInternalPaymentId] = useState(null);
  const [customerSessionSecret, setCustomerSessionSecret] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState('card');
  const [feeCents, setFeeCents] = useState(0);
  const [totalCents, setTotalCents] = useState(0);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('signin');
  const [savedBanks, setSavedBanks] = useState([]);
  const resumeHandled = useRef(false);
  // After /enroll-payer succeeds we route to the chooser, unless the user
  // landed here via the bank-gate signin (resume path 'ach'), in which case
  // we kick straight into the ACH PI flow.
  const pendingMethodAfterIdentity = useRef(null);
  // Track whether /enroll-payer is in flight to disable the Continue button.
  const [isEnrolling, setIsEnrolling] = useState(false);
  // Populated when the resume path detects this payer (by email) has already
  // completed/submitted a payment for this session. Surfaces a friendly
  // "already taken care of" screen instead of letting them pay again.
  const [alreadyPaidInfo, setAlreadyPaidInfo] = useState(null);

  // ── Fetch session ──
  useEffect(() => { fetchSession(); }, [sessionId]);

  const fetchSession = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/quick-split/public/${sessionId}`);
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'completed') { setErrorMsg('This split is fully paid.'); setStep('full'); }
        else if (data.status === 'expired') { setErrorMsg('This transaction has expired.'); setStep('expired'); }
        else if (data.status === 'closed' || data.status === 'cancelled') { setErrorMsg('This split has been closed.'); setStep('full'); }
        else if (data.link_expired || !data.link_active) { setErrorMsg('Payment link expired. Ask the host for a new one.'); setStep('link_expired'); }
        else {
          setSession(data);
          // Check for resume after auth (e.g., user returning from OAuth callback)
          const resume = loadResume();
          if (resume && resume.sessionId === sessionId && isAuthenticated) {
            applyResume(resume, { fromFetch: true });
            return;
          }

          // Returning-payer recovery: if this device has a stored enrollment
          // for this session, ask the server whether the payer already paid.
          //   - paid (any kind)  → friendly "already taken care of" screen
          //   - enrolled, unpaid → skip identity, drop straight to Step 3 with
          //                        name+email prefilled
          //   - nothing stored   → standard welcome flow
          //
          // Cross-device entry point: `?e=<email>` (set on reminder/confirmation
          // email return links). When present we treat it the same as a stored
          // enrollment and run the same /payer-status lookup so opening the
          // reminder from a different browser still resumes cleanly.
          let local = loadLocalEnrollment(sessionId);
          try {
            const params = new URLSearchParams(window.location.search);
            const e = params.get('e');
            if (e && isValidEmail(e)) {
              const lowered = e.trim().toLowerCase();
              local = local || { name: '', email: lowered, ts: Date.now() };
              if (!local.email) local.email = lowered;
              saveLocalEnrollment(sessionId, local.name || '', local.email);
              // Also seed React state so the identity screen renders prefilled
              // when the recovery path falls through to welcome (e.g. brand-
              // new device first opening a reminder link).
              if (!payerEmail) setPayerEmail(local.email);
              if (local.name && !payerName) setPayerName(local.name);
            }
          } catch { /* URLSearchParams unavailable / SSR — ignore */ }
          if (local?.email) {
            try {
              const statusRes = await fetch(
                `${BACKEND_URL}/api/quick-split/public/${sessionId}/payer-status?email=${encodeURIComponent(local.email)}`,
              );
              if (statusRes.ok) {
                const status = await statusRes.json();
                if (status.paid) {
                  setPayerName(local.name || status.payer_name || '');
                  setPayerEmail(local.email);
                  setAlreadyPaidInfo({
                    method: status.payment_method || null,
                    payment_status: status.payment_status || null,
                  });
                  setStep('already_paid');
                  return;
                }
                if (status.enrolled) {
                  setPayerName(local.name || status.payer_name || '');
                  setPayerEmail(local.email);
                  setIsGuest(!isAuthenticated);
                  goToStep(isAuthenticated ? 'payo_choose_method' : 'guest_choose_method');
                  return;
                }
              }
            } catch (e) {
              // Network blip — fall through to standard welcome. The user can
              // re-enter their identity; backend dedupes on (session_id,email).
              console.warn('payer-status lookup failed:', e);
            }
          }

          if (isAuthenticated) {
            setPayerName(authUser?.full_name || authUser?.name || '');
            if (authUser?.email) setPayerEmail(authUser.email);
            goToStep('welcome');
          } else {
            goToStep('welcome');
          }
        }
      } else if (res.status === 404) { setErrorMsg('Session not found'); setStep('error'); }
      else { const err = await res.json(); const d = err.detail; setErrorMsg(typeof d === 'string' ? d : 'Unable to load session'); setStep(res.status === 410 ? 'expired' : 'error'); }
    } catch { setErrorMsg('Unable to connect'); setStep('error'); }
  };

  // Centralized post-auth resume — invoked from both modal-close, redirect-callback, and fetchSession
  const applyResume = (resume, opts = {}) => {
    const { fromFetch = false } = opts;
    clearResume();
    resumeHandled.current = true;
    setIsGuest(false);
    const name = (resume?.payerName || authUser?.full_name || authUser?.name || '').trim();
    setPayerName(name);
    // Pre-fill account email when we have it so the identity step can be
    // confirmed with one tap.
    if (authUser?.email && !payerEmail) setPayerEmail(authUser.email);

    // Stash the post-identity intent so /enroll-payer → next-step routing
    // honours the resume context (e.g. user pre-selected Bank at the gate).
    if (resume?.path === 'ach') {
      pendingMethodAfterIdentity.current = 'ach';
    }
    goToStep('identity');
    void fromFetch;
  };

  // ── Resume after auth redirect (in-page password flow) ──
  useEffect(() => {
    if (resumeHandled.current) return;
    if (!session || !isAuthenticated) return;
    const resume = loadResume();
    if (!resume || resume.sessionId !== sessionId) return;
    applyResume(resume);
  }, [session, isAuthenticated]);

  const createIntent = async (name, authenticated, method, newBankOnly) => {
    setStep('creating_intent');
    try {
      const payload = { payer_name: name.trim() };
      // payer_email: explicit input takes priority; fall back to account email
      // for authenticated users so the backend always has a real receipt/reminder target.
      const emailInput = (payerEmail || '').trim();
      const accountEmail = (authUser?.email || '').trim();
      const finalEmail = emailInput || (authenticated ? accountEmail : '');
      if (finalEmail) payload.payer_email = finalEmail;
      if (authenticated && authUser?.id) payload.user_id = authUser.id;
      if (method) payload.method = method;
      const res = await fetch(`${BACKEND_URL}/api/quick-split/public/${sessionId}/pay`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const data = await res.json();
        setClientSecret(data.client_secret);
        setPaymentIntentId(data.payment_intent_id);
        setInternalPaymentId(data.internal_payment_id);
        setFeeCents(data.fee_cents || 0);
        setTotalCents(data.total_cents || data.split_cents || 0);
        if (data.method) setSelectedMethod(data.method);
        if (!newBankOnly && data.customer_session_client_secret) {
          setCustomerSessionSecret(data.customer_session_client_secret);
        } else if (newBankOnly) {
          setCustomerSessionSecret(null);
        }
        setStep(newBankOnly ? 'payo_new_bank' : 'paying');
        return;
      }

      // Error path — surface a readable error rather than silently dropping the user back to name entry
      let detail = '';
      try { const err = await res.json(); detail = typeof err.detail === 'string' ? err.detail : ''; } catch { /* ignore */ }

      if (res.status === 410) {
        setErrorMsg(detail || 'Session is no longer available');
        setStep('expired');
        return;
      }
      if (res.status === 409) {
        // Host's Connect account is not fully set up
        const msg = detail || "The host hasn't finished payout setup. Please ask them to complete it before you pay.";
        setErrorMsg(msg);
        toast.error("Payout setup incomplete", { description: msg });
      } else if (res.status === 503) {
        const msg = detail || "We couldn't verify the host's payout setup. Please try again in a moment.";
        setErrorMsg(msg);
        toast.error("Setup check failed", { description: msg });
      } else {
        const msg = detail || `Payment couldn't start (${res.status}). Please try again.`;
        setErrorMsg(msg);
        toast.error("Couldn't start payment", { description: msg });
      }
      // Return user to the relevant chooser step so they can retry or pick another method
      const safeStep = authenticated ? 'payo_choose_method' : 'guest_choose_method';
      setStep(safeStep);
    } catch {
      setErrorMsg('Network error. Please check your connection and try again.');
      toast.error('Network error', { description: "Couldn't reach Payo. Try again." });
      setStep(authenticated ? 'payo_choose_method' : 'guest_choose_method');
    }
  };

  // ── Handlers ──
  const handleContinueAsGuest = () => {
    setIsGuest(true);
    pendingMethodAfterIdentity.current = null;
    goToStep('identity');
  };

  const handleContinueWithPayo = () => {
    if (isAuthenticated) {
      setIsGuest(false);
      const name = authUser?.full_name || authUser?.name || '';
      const email = authUser?.email || '';
      if (name && !payerName) setPayerName(name);
      if (email && !payerEmail) setPayerEmail(email);
      pendingMethodAfterIdentity.current = null;
      goToStep('identity');
    } else {
      saveResume(sessionId, payerName, 'payo');
      sessionStorage.setItem('ach_return_url', `/quick-split/${sessionId}`);
      setAuthModalMode('signin');
      setShowAuthModal(true);
    }
  };

  // Step 2 → Step 3: enroll the payer for reminder/tracking THEN route to
  // the payment-choice screen. This is the reminder-enrollment checkpoint.
  const identityValid = () => payerName.trim().length > 0 && isValidEmail(payerEmail);

  const handleIdentityContinue = async () => {
    if (!identityValid() || isEnrolling) return;
    setIsEnrolling(true);
    try {
      // Best-effort enrollment — the payment endpoints also persist the email,
      // so even if enrollment fails the user can still pay. Reminder coverage
      // is the only thing we lose, and the toast surfaces that.
      const res = await fetch(`${BACKEND_URL}/api/quick-split/public/${sessionId}/enroll-payer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payer_name: payerName.trim(),
          payer_email: payerEmail.trim(),
          // When the payer is signed in, tag the participant row as a Payo
          // account vs guest. Backend never auto-creates an account here —
          // this is purely metadata for reminder/receipt routing + analytics.
          ...(isAuthenticated && authUser?.id ? { user_id: authUser.id } : {}),
        }),
      });
      if (!res.ok && res.status !== 400) {
        console.warn('enroll-payer non-2xx:', res.status);
      }
      if (res.status === 400) {
        toast.error('Please check your name and email and try again.');
        setIsEnrolling(false);
        return;
      }
      // Persist locally so returning to this link later resumes mid-flow.
      saveLocalEnrollment(sessionId, payerName.trim(), payerEmail.trim().toLowerCase());
    } catch (e) {
      console.warn('enroll-payer error:', e);
    } finally {
      setIsEnrolling(false);
    }

    // Route to the appropriate next step.
    const wantAch = pendingMethodAfterIdentity.current === 'ach';
    pendingMethodAfterIdentity.current = null;
    if (wantAch) {
      createIntent(payerName.trim(), true, 'ach', false);
      return;
    }
    goToStep(isGuest ? 'guest_choose_method' : 'payo_choose_method');
  };

  // Step 3 (chooser) handlers — same as before, but no longer responsible
  // for collecting identity (already enrolled in Step 2).
  const handleGuestPayCard = () => { createIntent(payerName.trim(), false, 'card'); };
  const handleGuestPayBank = () => { goToStep('guest_bank_gate'); };
  const handleGuestExternal = () => { goToStep('guest_external_confirm'); };

  // Venmo flow — NO API integration, NO prefill (Venmo's iOS app ignores
  // ?amount and ?note on universal links). We only open the host's stable
  // profile URL from the confirmation screen, where the payer reads the
  // amount/recipient/note off-screen and enters them manually in Venmo.
  const buildVenmoUrl = () => {
    const handle = session?.host_venmo_username;
    if (!handle) return null;
    return `https://venmo.com/u/${encodeURIComponent(handle)}`;
  };

  // Tile click: don't open Venmo yet — route to the confirm step where the
  // payer sees recipient/amount/split, taps Open Venmo to send, then comes
  // back and taps Yes, mark as paid.
  const handleVenmoTileClick = () => {
    goToStep('venmo_confirm');
  };

  const [isDeclaringExternal, setIsDeclaringExternal] = useState(false);
  const handleDeclareExternal = async (channel = null) => {
    setIsDeclaringExternal(true);
    try {
      const accountEmail = (authUser?.email || '').trim();
      const finalEmail = (payerEmail || '').trim() || accountEmail;
      const payload = { payer_name: payerName.trim(), payer_email: finalEmail };
      if (channel) payload.channel = channel;
      const res = await fetch(`${BACKEND_URL}/api/quick-split/public/${sessionId}/declare-external`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) goToStep('guest_external_done');
      else goToStep('guest_external_done');
    } catch { goToStep('guest_external_done'); }
    finally { setIsDeclaringExternal(false); }
  };

  const handleGuestBankAuth = (mode) => {
    // After auth we want to return to identity → Continue → ACH.
    saveResume(sessionId, payerName, 'ach');
    sessionStorage.setItem('ach_return_url', `/quick-split/${sessionId}`);
    setAuthModalMode(mode);
    setShowAuthModal(true);
  };

  const handleAuthModalClose = () => {
    setShowAuthModal(false);
    // If user authenticated inside the modal, use the same centralized resume logic.
    if (isAuthenticated) {
      if (resumeHandled.current) return; // useEffect path will pick it up
      const resume = loadResume();
      if (resume && resume.sessionId === sessionId) {
        applyResume(resume);
      } else {
        // No resume context — best-effort: drop to identity step with whatever
        // we know prefilled.
        setIsGuest(false);
        const name = authUser?.full_name || authUser?.name || payerName || '';
        const email = authUser?.email || '';
        if (name) setPayerName(name);
        if (email && !payerEmail) setPayerEmail(email);
        goToStep('identity');
      }
    }
  };

  const handleMethodChange = () => {};

  const appearance = { theme: 'stripe', variables: { colorPrimary: '#f59e0b', borderRadius: '8px' } };

  // Memoize Stripe Elements options to prevent remounting
  const stripeOptions = useMemo(() => {
    if (!clientSecret) return null;
    const opts = { clientSecret, appearance };
    if (customerSessionSecret) opts.customerSessionClientSecret = customerSessionSecret;
    return opts;
  }, [clientSecret, customerSessionSecret]);

  const stripeOptionsNoCustomer = useMemo(() => {
    if (!clientSecret) return null;
    return { clientSecret, appearance };
  }, [clientSecret]);

  const handleSuccess = (pid, name, hostName, amountCents) => {
    const emailParam = payerEmail ? `&email=${encodeURIComponent(payerEmail)}` : '';
    const descParam = session?.description ? `&desc=${encodeURIComponent(session.description)}` : '';
    const split = session?.per_person_amount_cents || amountCents;
    const feeParam = `&fee=${feeCents}&total=${totalCents || amountCents}&split=${split}&method=${selectedMethod}`;
    navigate(`/quick-split/${sessionId}/success?pid=${encodeURIComponent(pid)}&name=${encodeURIComponent(name)}&host=${encodeURIComponent(hostName)}&amount=${amountCents}${emailParam}${descParam}${feeParam}`);
  };
  const handlePending = (pid, name, amountCents) => {
    const split = session?.per_person_amount_cents || amountCents;
    const feeParam = `&fee=${feeCents}&total=${totalCents || amountCents}&split=${split}&method=${selectedMethod}`;
    navigate(`/quick-split/${sessionId}/success?pid=${encodeURIComponent(pid)}&name=${encodeURIComponent(name)}&amount=${amountCents}&pending=ach${feeParam}`);
  };

  const fmt = (c) => `$${(c / 100).toFixed(2)}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center p-4">
      <Card className="max-w-sm w-full shadow-lg" data-testid="qs-pay-page">
        <CardContent className="pt-8 pb-6 px-6">
          {/* Header: Payo logo on top, Quick Split text below */}
          <div className="flex flex-col items-center mb-6 space-y-3">
            <PayoLogo size="sm" />
            <span className="text-base font-bold text-gray-800">Quick Split</span>
          </div>

          {/* Loading */}
          {step === 'loading' && (
            <div className="flex flex-col items-center py-12" data-testid="qs-pay-loading">
              <Loader2 className="w-8 h-8 animate-spin text-amber-500 mb-3" />
              <p className="text-sm text-gray-500">Loading...</p>
            </div>
          )}

          {/* ════════ ALREADY PAID (returning payer recovery) ════════ */}
          {step === 'already_paid' && session && (() => {
            const ps = alreadyPaidInfo?.payment_status;
            const isExternal = ps === 'pending_external';
            const isPending = ps === 'pending_ach';
            const headline = isExternal
              ? `Thanks — ${session.host_name} is confirming this`
              : isPending
                ? "Your bank transfer is processing"
                : "You've already taken care of this";
            const sub = isExternal
              ? `${session.host_name} will mark your ${fmt(session.per_person_amount_cents)} as paid once they confirm.`
              : isPending
                ? `Your ${fmt(session.per_person_amount_cents)} bank transfer is on its way — usually settles in 2–3 business days.`
                : `Your ${fmt(session.per_person_amount_cents)} has been settled. A receipt has been sent to ${payerEmail}.`;
            return (
              <div className="space-y-5 text-center" data-testid="qs-already-paid">
                <div className={`w-14 h-14 ${isExternal || isPending ? 'bg-orange-50' : 'bg-green-50'} rounded-2xl flex items-center justify-center mx-auto`}>
                  <CheckCircle className={`w-7 h-7 ${isExternal || isPending ? 'text-orange-600' : 'text-green-600'}`} />
                </div>
                <h2 className="text-lg font-semibold text-gray-900" data-testid="qs-already-paid-headline">{headline}</h2>
                <p className="text-sm text-gray-500">{sub}</p>
                <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600 text-left">
                  <p><span className="text-gray-400">Split:</span> {session.description || 'Quick Split'}</p>
                  <p className="mt-1"><span className="text-gray-400">Host:</span> {session.host_name}</p>
                </div>
                <button
                  onClick={() => {
                    // Edge case: user thinks our record is wrong (e.g., bought
                    // the link from a friend). Clear local enrollment and let
                    // them re-enter the flow. Server-side guards still prevent
                    // a true duplicate charge for the same paid email.
                    try { localStorage.removeItem(`${ENROLL_LOCAL_PREFIX}${sessionId}`); } catch { /* */ }
                    setAlreadyPaidInfo(null);
                    setPayerName('');
                    setPayerEmail('');
                    goToStep('welcome');
                  }}
                  className="text-xs text-gray-400 hover:text-gray-600 underline"
                  data-testid="qs-already-paid-not-me"
                >
                  Not you? Start over
                </button>
              </div>
            );
          })()}

          {/* ════════ WELCOME (Step 1 — context only) ════════ */}
          {step === 'welcome' && session && (
            <div className="space-y-6" data-testid="qs-pay-welcome">
              <div className="text-center space-y-2">
                <p className="text-base text-gray-700 leading-snug" data-testid="qs-welcome-prompt">
                  <span className="font-semibold text-gray-900">{session.host_name}</span> is asking you to join a split{session.description ? <> for <span className="font-semibold text-gray-900">{session.description}</span></> : ''}.
                </p>
              </div>

              <div className="space-y-3 pt-1">
                <Button className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white text-base font-semibold" onClick={handleContinueAsGuest} data-testid="qs-welcome-guest-btn">
                  Continue as guest
                </Button>
                <Button variant="outline" className="w-full h-12 text-base" onClick={handleContinueWithPayo} data-testid="qs-welcome-payo-btn">
                  <UserCircle className="w-5 h-5 mr-2" />
                  Continue with Payo
                </Button>
              </div>

              <p className="text-xs text-gray-500 text-center leading-relaxed px-2" data-testid="qs-welcome-helper">
                You can pay with Payo or mark it paid externally.
              </p>
            </div>
          )}

          {/* ════════ IDENTITY (Step 2 — reminder enrollment checkpoint) ════════ */}
          {step === 'identity' && session && (() => {
            const amt = session.per_person_amount_cents;
            const emailDirty = (payerEmail || '').length > 0;
            const emailInvalid = emailDirty && !isValidEmail(payerEmail);
            const valid = identityValid();
            return (
            <div className="space-y-5" data-testid="qs-pay-identity">
              <div className="text-center">
                <p className="text-xs text-gray-400 uppercase tracking-wide">Your portion is</p>
                <p className="text-3xl font-bold text-gray-900 mt-1" data-testid="qs-identity-portion">{fmt(amt)}</p>
                {session.description && (
                  <p className="text-xs text-gray-400 mt-1">{session.description}</p>
                )}
              </div>

              <div className="space-y-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-600 flex items-center"><User className="w-3.5 h-3.5 mr-1 text-gray-400" />First name</label>
                  <Input
                    placeholder="e.g. Alex"
                    value={payerName}
                    onChange={(e) => setPayerName(e.target.value)}
                    maxLength={50}
                    className="h-11 text-base"
                    autoComplete="given-name"
                    data-testid="qs-identity-name-input"
                    onKeyDown={(e) => e.key === 'Enter' && valid && handleIdentityContinue()}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-600 flex items-center"><Mail className="w-3.5 h-3.5 mr-1 text-gray-400" />Email</label>
                  <Input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoCapitalize="off"
                    placeholder="you@example.com"
                    value={payerEmail}
                    onChange={(e) => setPayerEmail(e.target.value)}
                    maxLength={200}
                    className="h-11 text-base"
                    data-testid="qs-identity-email-input"
                    onKeyDown={(e) => e.key === 'Enter' && valid && handleIdentityContinue()}
                  />
                  {emailInvalid ? (
                    <p className="text-xs text-red-500" data-testid="qs-identity-email-error">Please enter a valid email.</p>
                  ) : (
                    <p className="text-xs text-gray-400" data-testid="qs-identity-email-helper">We'll only use this for this split and payment updates.</p>
                  )}
                </div>
              </div>

              <Button
                onClick={handleIdentityContinue}
                disabled={!valid || isEnrolling}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white text-base font-semibold"
                data-testid="qs-identity-continue-btn"
              >
                {isEnrolling ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Continuing...</> : 'Continue'}
              </Button>
              <button onClick={() => goToStep('welcome')} className="w-full text-center text-xs text-gray-400 hover:text-gray-600 py-1" data-testid="qs-identity-back">Back</button>
            </div>);
          })()}

          {/* ════════ GUEST CHOOSE METHOD (Step 3 — guest path) ════════ */}
          {step === 'guest_choose_method' && session && (() => {
            const amt = session.per_person_amount_cents;
            const cardFee = computePayerFee(amt, 'card').fee;
            const achFee = computePayerFee(amt, 'ach').fee;
            return (
            <div className="space-y-5" data-testid="qs-pay-guest-method">
              <div className="text-center">
                <p className="text-xs text-gray-400 uppercase tracking-wide">Your portion is</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{fmt(amt)}</p>
                {session.description && <p className="text-xs text-gray-400 mt-1">{session.description}</p>}
              </div>
              <p className="text-sm text-gray-600 text-center">How would you like to pay, {payerName}?</p>
              <div className="space-y-2">
                <button onClick={handleGuestPayCard} className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-colors" data-testid="qs-guest-pay-card">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0"><CreditCard className="w-5 h-5 text-blue-600" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">Pay instantly</p>
                      <p className="text-xs text-gray-400" data-testid="qs-guest-card-fee">Card &middot; Apple Pay &middot; {fmt(cardFee)} fee</p>
                    </div>
                  </div>
                </button>
                <button onClick={handleGuestPayBank} className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-green-400 hover:bg-green-50 transition-colors" data-testid="qs-guest-pay-bank">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0"><Building2 className="w-5 h-5 text-green-600" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">Pay by bank</p>
                      <p className="text-xs text-gray-400" data-testid="qs-guest-bank-fee">2&ndash;3 business days &middot; {fmt(achFee)} fee</p>
                    </div>
                  </div>
                </button>
                {session.host_venmo_username && (
                  <button
                    onClick={handleVenmoTileClick}
                    className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-sky-400 hover:bg-sky-50 transition-colors"
                    data-testid="qs-guest-pay-venmo"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-sky-600 font-bold text-base">V</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900">Pay via Venmo</p>
                        <p className="text-xs text-gray-400">No fees</p>
                      </div>
                    </div>
                  </button>
                )}
                <button onClick={handleGuestExternal} className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-colors" data-testid="qs-guest-mark-external">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0"><Banknote className="w-5 h-5 text-orange-600" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">Paid externally</p>
                      <p className="text-xs text-gray-400">Already paid?</p>
                    </div>
                  </div>
                </button>
              </div>
              <button onClick={() => goToStep('identity')} className="w-full text-center text-xs text-gray-400 hover:text-gray-600 py-1" data-testid="qs-guest-method-back">Back</button>
            </div>);
          })()}


          {/* ════════ GUEST EXTERNAL CONFIRM ════════ */}
          {step === 'guest_external_confirm' && session && (
            <div className="space-y-5 text-center" data-testid="qs-guest-external-confirm">
              <div className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center mx-auto">
                <Banknote className="w-7 h-7 text-orange-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Paid externally</h2>
              <p className="text-sm text-gray-500">{session.host_name} will confirm and mark this slot as paid.</p>
              <div className="bg-orange-50 rounded-lg p-3 text-sm text-orange-700">
                <p><span className="font-semibold">{session.host_name}</span> will mark your {fmt(session.per_person_amount_cents)} as paid once they confirm.</p>
              </div>
              <div className="space-y-3">
                <Button className="w-full h-11 bg-orange-500 hover:bg-orange-600 text-white" onClick={handleDeclareExternal} disabled={isDeclaringExternal} data-testid="qs-external-notify-btn">
                  {isDeclaringExternal ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Notify {session.host_name}
                </Button>
                <Button variant="outline" className="w-full" onClick={() => goToStep(isGuest ? 'guest_choose_method' : 'payo_choose_method')}>
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back to payment options
                </Button>
              </div>
            </div>
          )}

          {step === 'guest_external_done' && session && (
            <div className="space-y-5 text-center" data-testid="qs-guest-external-done">
              <div className="w-14 h-14 bg-green-50 rounded-2xl flex items-center justify-center mx-auto">
                <CheckCircle className="w-7 h-7 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">{session.host_name} has been notified.</h2>
              <p className="text-xs text-gray-400 mt-4">You can close this page.</p>
            </div>
          )}

          {/* ════════ VENMO CONFIRM (Step 4: payer returns from Venmo) ════════ */}
          {step === 'venmo_confirm' && session && (
            <div className="space-y-5" data-testid="qs-venmo-confirm">
              <h2 className="text-lg font-semibold text-gray-900 text-center" data-testid="qs-venmo-heading">Pay with Venmo</h2>

              {/* Show recipient / amount / split CLEARLY so the payer can
                  sanity-check before tapping Proceed (Venmo profile-only
                  link doesn't reliably prefill amount/note on iOS). */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl divide-y divide-gray-200 text-sm" data-testid="qs-venmo-summary">
                <div className="flex justify-between p-3">
                  <span className="text-gray-500">Recipient</span>
                  <span className="font-semibold text-gray-900" data-testid="qs-venmo-recipient">@{session.host_venmo_username}</span>
                </div>
                <div className="flex justify-between p-3">
                  <span className="text-gray-500">Amount</span>
                  <span className="font-semibold text-gray-900" data-testid="qs-venmo-amount">{fmt(session.per_person_amount_cents)}</span>
                </div>
                <div className="flex justify-between p-3">
                  <span className="text-gray-500">Split</span>
                  <span className="font-semibold text-gray-900 text-right max-w-[60%] truncate" data-testid="qs-venmo-split">{session.description || 'Quick Split'}</span>
                </div>
              </div>

              <p className="text-xs text-gray-500 text-center leading-relaxed">
                Send the payment in Venmo, then mark it as paid below.
              </p>

              <div className="space-y-2">
                {/* Sequential actions, not equal choices:
                    blue = go send the payment, green = confirm after sending. */}
                <a
                  href={buildVenmoUrl() || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full h-11 leading-[2.75rem] text-center bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium no-underline"
                  data-testid="qs-venmo-open"
                >
                  Proceed with Venmo
                </a>
                <Button
                  className="w-full h-11 bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => handleDeclareExternal('venmo')}
                  disabled={isDeclaringExternal}
                  data-testid="qs-venmo-mark-paid"
                >
                  {isDeclaringExternal ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Mark as Paid
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => goToStep(isGuest ? 'guest_choose_method' : 'payo_choose_method')}
                  data-testid="qs-venmo-cancel"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}


          {/* ════════ GUEST BANK GATE ════════ */}
          {step === 'guest_bank_gate' && session && (
            <div className="space-y-5 text-center" data-testid="qs-guest-bank-gate">
              <div className="w-14 h-14 bg-green-50 rounded-2xl flex items-center justify-center mx-auto">
                <Building2 className="w-7 h-7 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Bank transfer requires a Payo account</h2>
              <p className="text-sm text-gray-500">Sign in or create a free account to pay by bank with a lower fee.</p>
              <div className="space-y-3">
                <Button className="w-full h-11 bg-green-600 hover:bg-green-700 text-white" onClick={() => handleGuestBankAuth('signin')} data-testid="qs-bank-gate-signin">Sign in</Button>
                <Button variant="outline" className="w-full h-11" onClick={() => handleGuestBankAuth('signup')} data-testid="qs-bank-gate-create">Create account</Button>
                <Button variant="ghost" className="w-full text-gray-500" onClick={() => goToStep('guest_choose_method')} data-testid="qs-bank-gate-back">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back to payment options
                </Button>
              </div>
            </div>
          )}


          {/* ════════ PAYO METHOD CHOICE (Step 3 — signed-in path) ════════ */}
          {step === 'payo_choose_method' && session && (() => {
            const amt = session.per_person_amount_cents;
            const cardFee = computePayerFee(amt, 'card').fee;
            const achFee = computePayerFee(amt, 'ach').fee;
            return (
            <div className="space-y-5" data-testid="qs-payo-method-choice">
              <div className="text-center">
                <p className="text-sm text-gray-500">{session.description || 'Quick Split'}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{fmt(amt)}</p>
              </div>
              <p className="text-sm text-gray-600 text-center">How would you like to pay, {payerName}?</p>
              <div className="space-y-3">
                <button onClick={() => { setSelectedMethod('card'); createIntent(payerName.trim(), true, 'card'); }} className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-colors" data-testid="qs-payo-pay-card">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0"><CreditCard className="w-5 h-5 text-blue-600" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">Pay instantly</p>
                      <p className="text-xs text-gray-400" data-testid="qs-payo-card-fee">Card &middot; Apple Pay &middot; {fmt(cardFee)} fee</p>
                    </div>
                  </div>
                </button>
                <button onClick={async () => {
                  setSelectedMethod('ach');
                  // Fetch saved banks before proceeding
                  try {
                    const pmRes = await fetch(`${BACKEND_URL}/api/payment-methods`, { credentials: 'include' });
                    if (pmRes.ok) {
                      const pmData = await pmRes.json();
                      const banks = (pmData.payment_methods || []).filter(m => m.type === 'us_bank_account');
                      setSavedBanks(banks);
                      if (banks.length > 0) { goToStep('payo_saved_bank'); return; }
                    }
                  } catch (err) { console.error('Fetch saved PMs error:', err); }
                  // No saved banks — go directly to new bank setup
                  createIntent(payerName.trim(), true, 'ach');
                }} className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-green-400 hover:bg-green-50 transition-colors" data-testid="qs-payo-pay-bank">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0"><Building2 className="w-5 h-5 text-green-600" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">Pay by bank</p>
                      <p className="text-xs text-gray-400" data-testid="qs-payo-bank-fee">2&ndash;3 business days &middot; {fmt(achFee)} fee</p>
                    </div>
                  </div>
                </button>
                {session.host_venmo_username && (
                  <button
                    onClick={handleVenmoTileClick}
                    className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-sky-400 hover:bg-sky-50 transition-colors"
                    data-testid="qs-payo-pay-venmo"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-sky-600 font-bold text-base">V</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900">Pay via Venmo</p>
                        <p className="text-xs text-gray-400">No fees</p>
                      </div>
                    </div>
                  </button>
                )}
                <button onClick={handleGuestExternal} className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-colors" data-testid="qs-payo-mark-external">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0"><Banknote className="w-5 h-5 text-orange-600" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">Paid externally</p>
                      <p className="text-xs text-gray-400">Already paid?</p>
                    </div>
                  </div>
                </button>
              </div>
              <button onClick={() => goToStep('identity')} className="w-full text-center text-xs text-gray-400 hover:text-gray-600 py-1">Back</button>
            </div>);
          })()}


          {/* ════════ PAYO SAVED BANK (default ACH) ════════ */}
          {step === 'payo_saved_bank' && session && savedBanks.length > 0 && (() => {
            const bank = savedBanks[0];
            const amount = session.per_person_amount_cents;
            const { total, fee } = computePayerFee(amount, 'ach');
            return (
            <div className="space-y-5" data-testid="qs-payo-saved-bank">
              <div className="text-center">
                <p className="text-sm text-gray-500">{session.description || 'Quick Split'}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{fmt(amount)}</p>
              </div>

              {/* Saved bank card */}
              <div className="p-4 border-2 border-green-400 bg-green-50 rounded-xl" data-testid="qs-saved-bank-card">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0"><Building2 className="w-5 h-5 text-green-600" /></div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{bank.bank_name || 'Bank account'}</p>
                    <p className="text-xs text-gray-500">····{bank.last4} · {bank.account_type || 'checking'}</p>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-3 text-sm space-y-1" data-testid="qs-saved-bank-breakdown">
                  <div className="flex justify-between text-gray-600"><span>Split amount</span><span data-testid="qs-saved-bank-split">{fmt(amount)}</span></div>
                  <div className="flex justify-between text-gray-500"><span>Processing fee (Bank transfer)</span><span data-testid="qs-saved-bank-fee">{fmt(fee)}</span></div>
                  <div className="flex justify-between font-semibold text-gray-900 pt-2 border-t border-gray-200"><span>Total charged</span><span data-testid="qs-saved-bank-total">{fmt(total)}</span></div>
                </div>
              </div>

              <Button onClick={() => createIntent(payerName.trim(), true, 'ach')} className="w-full h-12 bg-green-600 hover:bg-green-700 text-white text-base font-semibold" data-testid="qs-saved-bank-pay-btn">
                Pay {fmt(total)} with saved bank
              </Button>

              <div className="flex items-center justify-center space-x-1.5 text-xs text-gray-400" data-testid="qs-saved-bank-stripe-trust">
                <ShieldCheck className="w-3 h-3" />
                <span>Secure payments powered by Stripe</span>
              </div>

              <button onClick={() => createIntent(payerName.trim(), true, 'ach', true)} className="w-full text-center text-xs text-gray-400 hover:text-green-600 py-1" data-testid="qs-use-different-bank">
                Use a different bank account
              </button>

              <button onClick={() => goToStep('payo_choose_method')} className="w-full text-center text-xs text-gray-400 hover:text-gray-600 py-1">Back</button>
            </div>);
          })()}



          {/* Creating intent spinner */}
          {step === 'creating_intent' && (
            <div className="flex flex-col items-center py-12"><Loader2 className="w-8 h-8 animate-spin text-amber-500 mb-3" /><p className="text-sm text-gray-500">Preparing payment...</p></div>
          )}

          {/* ════════ PAYO NEW BANK (no saved methods shown) ════════ */}
          {step === 'payo_new_bank' && clientSecret && session && (() => {
            const amount = session.per_person_amount_cents;
            const fee = feeCents;
            const total = totalCents || amount;
            return (
            <div data-testid="qs-payo-new-bank">
              <div className="text-center mb-4">
                <p className="text-base font-semibold text-gray-800">Connect a different bank</p>
                <p className="text-xs text-gray-400 mt-1">{session.description || 'Quick Split'} · {fmt(amount)}</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm space-y-1" data-testid="qs-new-bank-breakdown">
                <div className="flex justify-between text-gray-600"><span>Split amount</span><span data-testid="qs-new-bank-split">{fmt(amount)}</span></div>
                <div className="flex justify-between text-gray-500"><span>Processing fee (Bank transfer)</span><span data-testid="qs-new-bank-fee">{fmt(fee)}</span></div>
                <div className="flex justify-between font-semibold text-gray-900 pt-2 border-t border-gray-200"><span>Total charged</span><span data-testid="qs-new-bank-total">{fmt(total)}</span></div>
              </div>

              <Elements stripe={stripePromise} options={stripeOptionsNoCustomer}>
                <CheckoutForm sessionId={sessionId} amountCents={amount} displayTotal={total} paymentIntentId={paymentIntentId} internalPaymentId={internalPaymentId} payerName={payerName} onSuccess={handleSuccess} onPending={handlePending} isGuest={false} />
              </Elements>

              <button onClick={() => goToStep('payo_saved_bank')} className="w-full text-center text-xs text-gray-400 hover:text-gray-600 py-2 mt-2">Back to saved bank</button>
            </div>);
          })()}



          {/* ════════ PAYING ════════ */}
          {step === 'paying' && clientSecret && session && (() => {
            const amount = session.per_person_amount_cents;
            const fee = feeCents;
            const total = totalCents || amount;
            const methodLabel = METHOD_LABEL[selectedMethod] || 'Card';
            return (
            <div data-testid="qs-pay-form">
              <div className="text-center mb-4">
                <p className="text-sm text-gray-500">Hi {payerName}!</p>
                <p className="text-2xl font-bold text-gray-900">{fmt(amount)}</p>
                <p className="text-xs text-gray-400">{session.description || 'Quick Split'} · {session.host_name}</p>
              </div>

              {/* Fee breakdown — payer covers processing fee; host gets exact split */}
              <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm space-y-1" data-testid="qs-pay-fee-breakdown">
                <div className="flex justify-between text-gray-600"><span>Split amount</span><span data-testid="qs-pay-split">{fmt(amount)}</span></div>
                <div className="flex justify-between text-gray-500"><span>Processing fee ({methodLabel})</span><span data-testid="qs-pay-fee">{fmt(fee)}</span></div>
                <div className="flex justify-between font-semibold text-gray-900 pt-2 border-t border-gray-200"><span>Total charged</span><span data-testid="qs-pay-total-charged">{fmt(total)}</span></div>
              </div>

              <Elements stripe={stripePromise} options={stripeOptions}>
                <CheckoutForm sessionId={sessionId} amountCents={amount} displayTotal={total} paymentIntentId={paymentIntentId} internalPaymentId={internalPaymentId} payerName={payerName} onSuccess={handleSuccess} onPending={handlePending} isGuest={isGuest} />
              </Elements>
            </div>);
          })()}

          {/* Error states */}
          {step === 'full' && (<div className="text-center py-8" data-testid="qs-pay-full"><AlertCircle className="w-12 h-12 text-blue-400 mx-auto mb-3" /><p className="text-lg font-semibold text-gray-700">Session Complete</p><p className="text-sm text-gray-500 mt-2">{errorMsg}</p></div>)}
          {step === 'link_expired' && (<div className="text-center py-8" data-testid="qs-pay-link-expired"><Clock className="w-12 h-12 text-amber-400 mx-auto mb-3" /><p className="text-lg font-semibold text-gray-700">Payment Link Expired</p><p className="text-sm text-gray-500 mt-2">{errorMsg}</p></div>)}
          {step === 'expired' && (<div className="text-center py-8" data-testid="qs-pay-expired"><Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" /><p className="text-lg font-semibold text-gray-700">Session Expired</p><p className="text-sm text-gray-500 mt-2">{errorMsg}</p></div>)}
          {step === 'error' && (<div className="text-center py-8" data-testid="qs-pay-error-page"><AlertCircle className="w-12 h-12 text-red-300 mx-auto mb-3" /><p className="text-lg font-semibold text-gray-700">Something went wrong</p><p className="text-sm text-gray-500 mt-2">{errorMsg}</p></div>)}
        </CardContent>
      </Card>
      <CompleteAuthModal isOpen={showAuthModal} onClose={handleAuthModalClose} mode={authModalMode} />
    </div>
  );
};

export default QuickSplitPayPage;
